import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  Briefcase,
  Building2,
  CheckCircle2,
  Clock,
  FileText,
  Globe,
  LinkIcon,
  Mail,
  MapPin,
  Phone,
  Send,
  Shield,
  Sparkles,
  Target,
  Users,
  Zap,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SeoPackagesCompare from '../components/billing/SeoPackagesCompare';
import { PACKAGES } from '../data/seoPackages';

const FEATURES = [
  {
    icon: Target,
    title: 'AI-Powered SEO Research',
    description: 'Use AI-assisted keyword discovery, competitor analysis, content gap research, and opportunity scoring to build smarter SEO campaigns faster.'
  },
  {
    icon: BarChart3,
    title: 'Pay Per Click Advertising',
    description: 'Targeted PPC campaigns across search and social channels, built to drive qualified traffic and conversions.'
  },
  {
    icon: Globe,
    title: 'Website Development',
    description: 'Responsive WordPress, Shopify, WooCommerce, and Magento websites designed for performance and conversion.'
  },
  {
    icon: FileText,
    title: 'Content Creation',
    description: 'Blog posts, articles, graphics, and videos that engage your audience and support organic growth.'
  },
  {
    icon: Shield,
    title: 'Reputation Management',
    description: 'Review monitoring, customer feedback support, and strategies that protect and strengthen your brand.'
  },
  {
    icon: LinkIcon,
    title: 'Google Maps Marketing',
    description: 'Local visibility work that improves your presence in map results and helps nearby customers find you.'
  }
];

const STATS = [
  { value: 'SEO', label: 'Organic Growth' },
  { value: 'PPC', label: 'Paid Campaigns' },
  { value: 'Web', label: 'Development' },
  { value: 'ORM', label: 'Reputation Care' }
];

const NAV_LINKS = [
  { label: 'Features',     href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Pricing',      href: '#pricing' },
  { label: 'Compare',      href: '#seo-packages' },
  { label: 'About',        href: '#about' },
  { label: 'Careers',      href: '#careers' },
  { label: 'Contact',      href: '#contact' },
  { label: 'Legal',        href: '#legal' },
];

const ABOUT_VALUES = [
  { icon: Target, title: 'Tailored Strategies', description: 'Every plan is shaped around the client’s goals, market, audience, and budget.' },
  { icon: Shield, title: 'Transparent Service', description: 'We keep clients involved with clear communication, reporting, and practical recommendations.' },
  { icon: Zap, title: 'Data-Driven Execution', description: 'Research, analytics, and continuous optimization guide the work we deliver.' },
  { icon: Users, title: 'Long-Term Partnership', description: 'We work with clients as a dedicated digital growth partner, not a one-off vendor.' },
];

const CAREER_OPENINGS = [
  {
    title: 'Digital Marketing Specialists',
    team: 'Marketing',
    location: 'Hybrid / Remote',
    type: 'Open inquiry',
    description: 'SEO, PPC, content, analytics, and campaign specialists who care about measurable client outcomes.',
  },
  {
    title: 'Web Designers & Developers',
    team: 'Web',
    location: 'Hybrid / Remote',
    type: 'Open inquiry',
    description: 'Designers and developers experienced with responsive websites, CMS platforms, and e-commerce builds.',
  },
  {
    title: 'Client Support Partners',
    team: 'Client Success',
    location: 'Hybrid / Remote',
    type: 'Open inquiry',
    description: 'People who communicate clearly, support client questions, and keep digital projects moving.',
  },
];

const CAREER_PERKS = [
  'Work with a practical digital marketing agency serving growing businesses',
  'Collaborate across SEO, PPC, content, web development, and reputation projects',
  'Bring data-driven ideas that improve client visibility and conversions',
  'Send open applications to info@godigitalstep.com',
];

const LEGAL_SUMMARIES = [
  {
    id: 'privacy',
    href: '/privacy-policy',
    icon: Shield,
    title: 'Privacy Policy',
    description: 'How Go Digital Step collects, uses, protects, and shares personal information.',
    points: ['We collect details provided through forms and service interactions.', 'We do not sell personal information.', 'SMS opt-in data and phone numbers are not shared with third parties.'],
  },
  {
    id: 'terms',
    href: '/terms-and-conditions',
    icon: FileText,
    title: 'Terms and Conditions',
    description: 'The service, billing, cancellation, SEO, confidentiality, and client access terms for Go Digital Step.',
    points: ['SEO outcomes can depend on factors outside our control.', 'Fees are payable in advance and non-refundable.', 'Subscriptions renew until canceled under the stated policy.'],
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', company: '', message: '' });

  useEffect(() => {
    if (!location.hash) return;

    window.requestAnimationFrame(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, [location.hash]);

  const handleContactSubmit = (event) => {
    event.preventDefault();
    setContactSubmitted(true);
  };

  const handleContactChange = (event) => {
    setContactForm(prev => ({ ...prev, [event.target.name]: event.target.value }));
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 [scroll-padding-top:5rem]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 gds-glass border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl">GDS</span>
          </Link>

          {/* Desktop links */}
          <div className="hidden xl:flex items-center gap-5">
            {NAV_LINKS.map(({ label, href }) => (
              <a key={label} href={href} className="text-sm text-slate-600 hover:text-indigo-600 transition-colors font-medium">
                {label}
              </a>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden xl:flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login-btn">
              Sign In
            </Button>
            <Button className="gds-btn-primary" onClick={() => navigate('/register')} data-testid="nav-get-started-btn">
              Get Started
            </Button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="xl:hidden p-2 rounded-lg text-slate-600 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="xl:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-6 py-4 space-y-1">
            {NAV_LINKS.map(({ label, href }) => (
              <a
                key={label}
                href={href}
                onClick={() => setMobileOpen(false)}
                className="block py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-indigo-600 transition-colors"
              >
                {label}
              </a>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              <Button variant="outline" className="w-full" onClick={() => { navigate('/login'); setMobileOpen(false); }}>
                Sign In
              </Button>
              <Button className="gds-btn-primary w-full" onClick={() => { navigate('/register'); setMobileOpen(false); }}>
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-white to-violet-50/50 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-indigo-500/10 to-violet-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto relative">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              AI-Powered Digital Marketing OS
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-slate-900 dark:text-white">
              Your AI-Powered{' '}
              <span className="text-indigo-600 dark:text-indigo-400">Digital Marketing</span>{' '}
              Operating System
            </h1>
            
            <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto">
              Strategy, execution, approvals, proof, and reporting — all in one transparent platform.
              Always know what's being done, why, and its impact.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                className="gds-btn-primary text-lg px-8 py-6"
                onClick={() => navigate('/free-audit')}
                data-testid="hero-get-started-btn"
              >
                Book a Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="gds-btn-secondary text-lg px-8 py-6"
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                data-testid="hero-signup-btn"
              >
                View Growth Plans
              </Button>
            </div>
          </motion.div>
          
          {/* Dashboard Preview */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 relative"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent dark:from-slate-950 z-10 h-40 bottom-0 top-auto" />
            <div className="bg-slate-900 rounded-2xl shadow-2xl shadow-slate-900/20 overflow-hidden border border-slate-800">
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-800 border-b border-slate-700">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="ml-4 text-sm text-slate-400">Go Digital Step Services</span>
              </div>
              <div className="p-6 bg-slate-900">
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {STATS.map((stat, i) => (
                    <div key={i} className="bg-slate-800 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-white">{stat.value}</div>
                      <div className="text-sm text-slate-400">{stat.label}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 bg-slate-800 rounded-xl p-4 h-48">
                    <div className="text-sm text-slate-400 mb-2">Campaign Focus</div>
                    <div className="flex items-end justify-between h-32 gap-2">
                      {[40, 55, 45, 60, 75, 65, 85, 90, 80, 95, 88, 100].map((h, i) => (
                        <div key={i} className="flex-1 bg-gradient-to-t from-indigo-600 to-violet-500 rounded-t" style={{ height: `${h}%` }} />
                      ))}
                    </div>
                  </div>
                  <div className="bg-slate-800 rounded-xl p-4">
                    <div className="text-sm text-slate-400 mb-2">Digital Readiness</div>
                    <div className="flex items-center justify-center h-32">
                      <div className="relative w-28 h-28">
                        <svg className="w-28 h-28 transform -rotate-90">
                          <circle cx="56" cy="56" r="48" stroke="#334155" strokeWidth="8" fill="none" />
                          <circle cx="56" cy="56" r="48" stroke="url(#gradient)" strokeWidth="8" fill="none" strokeDasharray={`${78 * 3.01} ${100 * 3.01}`} strokeLinecap="round" />
                          <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#6366f1" />
                              <stop offset="100%" stopColor="#8b5cf6" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-3xl font-bold text-white">SEO</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-slate-50 dark:bg-slate-900/50 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
              Comprehensive Digital{' '}
              <span className="text-indigo-600 dark:text-indigo-400">Marketing Services</span>
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Practical, affordable solutions for visibility, traffic, leads, and online reputation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="gds-card-interactive h-full">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-6 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
              How <span className="text-indigo-600 dark:text-indigo-400">GDS</span> Works
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Simple, transparent, effective. Here's how we drive results.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Connect', desc: 'Link your GA4, GSC, and GBP accounts in minutes' },
              { step: '02', title: 'Research', desc: 'AI analyzes your market, competitors, and opportunities' },
              { step: '03', title: 'Approve', desc: 'Review and approve all strategies and content' },
              { step: '04', title: 'Grow', desc: 'Watch your traffic and rankings climb with full proof' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                viewport={{ once: true }}
                className="text-center relative"
              >
                {i < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-indigo-500/50 to-transparent" />
                )}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-500/30">
                  <span className="font-heading font-bold text-white text-xl">{item.step}</span>
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-slate-50 dark:bg-slate-900/50 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
              Choose Your <span className="text-indigo-600 dark:text-indigo-400">Growth Plan</span>
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Flexible digital marketing plans with transparent deliverables and clear expectations.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-4 gap-8">
            {PACKAGES.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                viewport={{ once: true }}
                onMouseEnter={() => setHoveredPlan(i)}
                onMouseLeave={() => setHoveredPlan(null)}
              >
                <Card className={`relative overflow-hidden h-full transition-all duration-300 ${
                  plan.popular 
                    ? 'border-2 border-indigo-500 shadow-xl shadow-indigo-500/10' 
                    : 'border border-slate-200 dark:border-slate-800'
                } ${hoveredPlan === i ? 'scale-105' : ''}`}>
                  {plan.badge && (
                    <div className={`absolute top-0 right-0 px-4 py-1 text-xs font-medium ${
                      plan.popular
                        ? 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white' 
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                    }`}>
                      {plan.badge}
                    </div>
                  )}
                  <CardContent className="p-8">
                    <h3 className="font-heading font-bold text-xl mb-2">{plan.name}</h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">{plan.description}</p>
                    
                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="font-heading text-4xl font-bold">${plan.price.toLocaleString()}</span>
                      <span className="text-slate-500">{plan.period}</span>
                    </div>
                    
                    <Button 
                      className={`w-full mb-6 ${plan.popular ? 'gds-btn-primary' : 'gds-btn-secondary'}`}
                      onClick={() => navigate(`/register?package=${plan.id}`)}
                      data-testid={`pricing-${plan.name.toLowerCase().replace(' ', '-')}-btn`}
                    >
                      Get Started
                      <ChevronRight className="ml-2 w-4 h-4" />
                    </Button>
                    
                    <div className="space-y-3">
                      {plan.highlights.map((feature, j) => (
                        <div key={j} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-600 dark:text-slate-400">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a href="#seo-packages" className="text-indigo-600 hover:text-indigo-500 font-medium">
              See full comparison ->
            </a>
          </div>
        </div>
      </section>

      {/* Full Package Comparison */}
      <section id="seo-packages" className="py-20 px-6 scroll-mt-20">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
              Full SEO <span className="text-indigo-600 dark:text-indigo-400">Deliverables Matrix</span>
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              Compare Basic, Silver, Gold, and Platinum plans in one scrollable section.
            </p>
          </div>

          <SeoPackagesCompare
            onSubscribe={(packageId) => navigate(`/register?package=${packageId}`)}
            title="Everything Included in Each Plan"
            subtitle="Analysis, on-page, off-page, content, local SEO, reporting, and execution deliverables side by side."
            showHeader
          />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-slate-50 dark:bg-slate-900/50 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center mb-14">
            <div>
              <div className="inline-flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Globe className="w-4 h-4" />
                Serving Businesses Across the USA
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-5 text-slate-900 dark:text-white">
                Leading digital marketing <span className="text-indigo-600 dark:text-indigo-400">excellence</span>
              </h2>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                Go Digital Step helps businesses turn their online presence into a stronger source of visibility,
                engagement, and revenue through tailored digital marketing strategies.
              </p>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                Our approach combines data-driven insight with creative execution so each SEO, PPC, content,
                website, or reputation project is aligned with the client’s specific goals.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {ABOUT_VALUES.map(({ icon: Icon, title, description }) => (
                <Card key={title} className="gds-card-interactive h-full">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2 text-slate-900 dark:text-white">{title}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: 'SEO', label: 'Search Visibility' },
              { value: 'PPC', label: 'Paid Advertising' },
              { value: 'Web', label: 'Design & Development' },
              { value: 'ORM', label: 'Reputation Management' },
            ].map(({ value, label }) => (
              <div key={label} className="rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-6">
                <p className="font-heading font-bold text-3xl text-slate-900 dark:text-white">{value}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section id="careers" className="py-20 px-6 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12">
            <div>
              <div className="inline-flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Briefcase className="w-4 h-4" />
                Careers & Collaboration
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
                Grow with <span className="text-indigo-600 dark:text-indigo-400">Go Digital Step</span>
              </h2>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
                We do not currently publish fixed job openings on the live site, but we welcome thoughtful
                inquiries from digital marketing, content, design, development, and client support professionals.
              </p>
              <div className="space-y-3">
                {CAREER_PERKS.map((perk) => (
                  <div key={perk} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">{perk}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {CAREER_OPENINGS.map(({ title, team, location, type, description }) => (
                <Card key={title} className="gds-card-interactive">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-slate-900 dark:text-white">{title}</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{description}</p>
                        <div className="flex flex-wrap items-center gap-4 mt-3">
                          <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                            <Briefcase className="w-3 h-3" /> {team}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                            <MapPin className="w-3 h-3" /> {location}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                            <Clock className="w-3 h-3" /> {type}
                          </span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="shrink-0" asChild>
                        <a href={`mailto:info@godigitalstep.com?subject=Career inquiry: ${encodeURIComponent(title)}`}>
                          Inquire <ArrowRight className="w-4 h-4 ml-1" />
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-slate-50 dark:bg-slate-900/50 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
              Get in <span className="text-indigo-600 dark:text-indigo-400">Touch</span>
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              Questions, consultations, or project ideas — we would be happy to assist you.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              {[
                { icon: Mail, label: 'Email', value: 'info@godigitalstep.com', href: 'mailto:info@godigitalstep.com' },
                { icon: Phone, label: 'Phone', value: '(302) 303-4272', href: 'tel:+13023034272' },
                { icon: MapPin, label: 'Location', value: 'India', href: null },
              ].map(({ icon: Icon, label, value, href }) => (
                <Card key={label} className="gds-card">
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{label}</p>
                      {href ? (
                        <a href={href} className="text-sm text-indigo-600 hover:underline">{value}</a>
                      ) : (
                        <p className="text-sm text-slate-600 dark:text-slate-400">{value}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}

              <div className="p-6 rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800">
                <h3 className="font-heading font-semibold text-slate-900 dark:text-white mb-2">Support hours</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Please call or email us and our team will be happy to assist you.
                </p>
              </div>
            </div>

            <Card className="gds-card">
              <CardContent className="p-6">
                {contactSubmitted ? (
                  <div className="flex flex-col items-center justify-center text-center py-16">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="font-heading font-semibold text-xl text-slate-900 dark:text-white mb-2">
                      Message sent!
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400">
                      Thanks for reaching out. We will get back to you within one business day.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={contactForm.name}
                          onChange={handleContactChange}
                          placeholder="Your full name"
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={contactForm.email}
                          onChange={handleContactChange}
                          placeholder="you@company.com"
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Company
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          name="company"
                          value={contactForm.company}
                          onChange={handleContactChange}
                          placeholder="Your company"
                          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        value={contactForm.message}
                        onChange={handleContactChange}
                        placeholder="Tell us how we can help..."
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm resize-none"
                      />
                    </div>
                    <Button type="submit" className="gds-btn-primary w-full flex items-center justify-center gap-2">
                      <Send className="w-4 h-4" /> Send Message
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Legal Section */}
      <section id="legal" className="py-20 px-6 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
              Privacy & <span className="text-indigo-600 dark:text-indigo-400">Terms</span>
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              A practical summary of our live Privacy Policy and Terms and Conditions.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {LEGAL_SUMMARIES.map(({ id, href, icon: Icon, title, description, points }) => (
              <Card
                key={id}
                id={id}
                role="link"
                tabIndex={0}
                onClick={() => navigate(href)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(href);
                  }
                }}
                className="gds-card gds-card-interactive scroll-mt-28 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-slate-950"
              >
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-slate-900 dark:text-white mb-3">{title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-5">{description}</p>
                  <div className="space-y-3">
                    {points.map((point) => (
                      <div key={point} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">{point}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Questions about {title.toLowerCase()}? Email{' '}
                      <a
                        href="mailto:info@godigitalstep.com"
                        onClick={(event) => event.stopPropagation()}
                        className="text-indigo-600 hover:underline"
                      >
                        info@godigitalstep.com
                      </a>
                    </p>
                  </div>
                  <div className="mt-5 inline-flex items-center text-sm font-medium text-indigo-600">
                    Read full {title}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="gds-card bg-gradient-to-r from-indigo-500 to-violet-600 text-white p-12 rounded-3xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                  Ready to Dominate Your Market?
                </h2>
                <p className="text-indigo-100 mb-6">
                  Talk with Go Digital Step about SEO, PPC, content, website development, or reputation management.
                </p>
                <div className="flex items-center gap-4">
                  <Button 
                    size="lg" 
                    className="bg-white text-indigo-600 hover:bg-indigo-50 rounded-full px-8"
                    onClick={() => navigate('/free-audit')}
                    data-testid="cta-free-audit-btn"
                  >
                    Get Free Audit
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {['SEO', 'PPC', 'Web Development', 'Content Creation'].map((service) => (
                  <div key={service} className="rounded-2xl bg-white/15 px-4 py-3 text-center font-medium">
                    {service}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-bold text-xl">GDS</span>
              </Link>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Digital marketing services for businesses ready to improve visibility, traffic, and growth.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li><a href="#features" className="hover:text-indigo-600">Services</a></li>
                <li><a href="#pricing" className="hover:text-indigo-600">Pricing</a></li>
                <li><a href="#seo-packages" className="hover:text-indigo-600">Package Comparison</a></li>
                <li><a href="#how-it-works" className="hover:text-indigo-600">How It Works</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li><a href="#about" className="hover:text-indigo-600">About Us</a></li>
                <li><a href="#careers" className="hover:text-indigo-600">Careers</a></li>
                <li><a href="#contact" className="hover:text-indigo-600">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li><Link to="/privacy-policy" className="hover:text-indigo-600">Privacy Policy</Link></li>
                <li><Link to="/terms-and-conditions" className="hover:text-indigo-600">Terms and Conditions</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              © 2026 GDS - Go Digital Step. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <Users className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-600 dark:text-slate-400">India</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
